import { type BlockText } from "@do/shared-types";
import { cn } from "@do/ui/lib/utils";
import { useMemo } from "react";
import remend from "remend";
import { MarkdownRenderer } from "../../markdown-renderer";

type MessageTextProps = {
  block: BlockText;
  metadata?: string | Record<string, unknown>[] | null | undefined;
  messageId: string | undefined;
  messageIndex: number;
  id: string;
  idx: number;
  isUser: boolean;
  handleImageLoad?: () => void;
  isErrorMessage?: boolean;
  isConsecutiveUserMessage?: boolean;
  isFollowedByUserMessage?: boolean;
  isConsecutiveBotTextBlock?: boolean;
  isFollowedByBotTextBlock?: boolean;
};

export const MessageText = ({
  id,
  idx,
  block,
  messageId,
  isUser,
  handleImageLoad,
  isErrorMessage,
  isConsecutiveUserMessage,
  isFollowedByUserMessage,
  isConsecutiveBotTextBlock,
  isFollowedByBotTextBlock,
}: MessageTextProps) => {
  const userBubbleCornerClass = useMemo(() => {
    if (!isUser) return "";

    // Bubble "tail" shaping for grouped consecutive user messages:
    // - first in group: round bottom-right
    // - middle in group: round both top-right and bottom-right
    // - last in group: round top-right
    const hasPrevUserMessage = Boolean(isConsecutiveUserMessage);
    const hasNextUserMessage = Boolean(isFollowedByUserMessage);

    if (hasPrevUserMessage && hasNextUserMessage) {
      return cn(
        "rounded-tr-(--message-user-text-border-radius-bubble)",
        "rounded-br-(--message-user-text-border-radius-bubble)"
      );
    }

    if (hasPrevUserMessage) {
      return "rounded-tr-(--message-user-text-border-radius-bubble)";
    }

    return "rounded-br-(--message-user-text-border-radius-bubble)";
  }, [isConsecutiveUserMessage, isFollowedByUserMessage, isUser]);

  const botBubbleCornerClass = useMemo(() => {
    if (isUser) return "";

    // Bubble "tail" shaping for grouped consecutive bot text blocks:
    // - first in group: round bottom-left
    // - middle in group: round both top-left and bottom-left
    // - last in group: round top-left
    const hasPrevBotText = Boolean(isConsecutiveBotTextBlock);
    const hasNextBotText = Boolean(isFollowedByBotTextBlock);

    if (hasPrevBotText && hasNextBotText) {
      return cn(
        "rounded-tl-(--message-bot-text-border-radius-bubble)",
        "rounded-bl-(--message-bot-text-border-radius-bubble)"
      );
    }

    if (hasPrevBotText) {
      return "rounded-tl-(--message-bot-text-border-radius-bubble)";
    }

    return "rounded-bl-(--message-bot-text-border-radius-bubble)";
  }, [isConsecutiveBotTextBlock, isFollowedByBotTextBlock, isUser]);

  const renderedText = useMemo(() => {
    const raw = block.text;
    // Don't display a dangling markdown bold marker at the end.
    // e.g. "Hello, I'm **" -> "Hello, I'm "
    // but keep "Hello, I'm **amaz"
    const cleaned = raw.replace(/\*\*\s*$/, "");
    return remend(cleaned);
  }, [block.text]);

  return (
    <div
      id={`message-${messageId}-block-${id}-${idx}`}
      className={cn(
        "chat-message-text",
        isUser
          ? cn(
              "chat-message-text-user ml-auto w-fit break-words hyphens-auto",
              "font-(family-name:--message-user-text-font-family)",
              "font-(--message-user-text-font-weight)!",
              "leading-(--message-user-text-line-height)",
              "text-(length:--message-user-text-font-size)",
              "py-(--message-user-text-padding-y)",
              "px-(--message-user-text-padding-x)",
              "max-w-(--message-user-text-max-width)",
              "bg-(--message-user-text-background)/(--message-user-text-background-opacity)",
              "text-(--message-user-text-foreground)/(--message-user-text-foreground-opacity)",
              "border-(length:--message-user-text-border-width)",
              "border-(--message-user-text-border-color)/(--message-user-text-border-opacity)",
              "rounded-(--message-user-text-border-radius)",
              "[box-shadow:var(--message-user-text-box-shadow)]",
              userBubbleCornerClass
            )
          : cn(
              "chat-message-text-bot w-fit min-w-0 max-w-full break-words hyphens-auto",
              "[&_.markdown-renderer]:font-(family-name:--message-bot-text-font-family)",
              "[&_.markdown-renderer]:font-(--message-bot-text-font-weight)!",
              "[&_.markdown-renderer]:leading-(--message-bot-text-line-height)",
              "[&_.markdown-renderer]:text-(length:--message-bot-text-font-size)",
              "py-(--message-bot-text-padding-y)",
              "px-(--message-bot-text-padding-x)",
              "max-w-(--message-bot-text-max-width)",
              "bg-(--message-bot-text-background)/(--message-bot-text-background-opacity)",
              "[&_.markdown-renderer]:text-(--message-bot-text-foreground)/(--message-bot-text-foreground-opacity)",
              "border-(length:--message-bot-text-border-width)",
              "border-(--message-bot-text-border-color)/(--message-bot-text-border-opacity)",
              "rounded-(--message-bot-text-border-radius)",
              "rounded-bl-(--message-bot-text-border-radius-bubble)",
              "[box-shadow:var(--message-bot-text-box-shadow)]",
              botBubbleCornerClass,
              isErrorMessage ? "[&_.markdown-renderer]:text-(--error-color)!" : ""
            )
      )}
      role={isErrorMessage ? "alert" : undefined}
    >
      {isUser ? (
        block.text
      ) : (
        <MarkdownRenderer allowFullScreenImg text={renderedText} onImageLoad={handleImageLoad} />
      )}
    </div>
  );
};
